import React, { useEffect, useState } from 'react';
import './ProfileModal.css';
import { financialService } from '../../services/financialService';
import { userService } from '../../services/userService';
import { useAuth } from '../../state/AuthContext';
import PlaidAccountsList from '../Plaid/PlaidAccountsList';
import BillingDashboard from '../Billing/BillingDashboard';
import Skeleton from '../common/Skeleton/Skeleton';
import InlineAlert from '../common/InlineAlert';
import { getUserFacingError } from '../../lib/userFacingErrors';

const resources = [
  {
    key: 'account',
    label: 'Account',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.75"/>
        <path d="M4 20c0-3.87 3.58-7 8-7s8 3.13 8 7" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.75"/>
        <path d="M2 10h20" stroke="currentColor" strokeWidth="1.75"/>
      </svg>
    ),
  },
  {
    key: 'plaid',
    label: 'Connected Accounts',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="1.75"/>
        <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    key: 'intake',
    label: 'Financial Info',
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 20V10M12 7V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75"/>
      </svg>
    ),
  },
];

export default function ProfileModal({
  open,
  onClose,
  initialTab = 'account',
  onTabChange,
  needsPlaidConnect = false,
  onDismissPlaidConnect,
  onUpgradeRequired,
}) {
  const { session, logout, billingStatus, refreshBilling } = useAuth();
  const billingNotSetUp = billingStatus?.billing_status === 'setup_required';
  const [tab, setTab] = useState(initialTab || resources[0].key);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Account & Intake state
  const [account, setAccount] = useState(null);
  const [accountSaving, setAccountSaving] = useState(false);
  const [intake, setIntake] = useState(null);
  const [intakeSaving, setIntakeSaving] = useState(false);
  const [intakeErrors, setIntakeErrors] = useState({});
  const [plaidRefreshKey, setPlaidRefreshKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTab(initialTab || resources[0].key);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setLoadError(null);
    (async () => {
      if (tab === 'account') await loadAccount();
      else if (tab === 'intake') await loadIntake();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  // ESC key to close modal
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  async function loadAccount() {
    if (!session?.userId) return;
    setLoading(true);
    setError(null);
    setLoadError(null);
    try {
      const row = await userService.getUser(session.userId);
      const phone = row.phone || '';
      const digits = phone.replace(/\D/g, '');
      // Handle US numbers with country code +1 (11 digits) or without (10 digits)
      const usDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
      let formattedPhone = phone;
      if (usDigits.length === 10) {
        formattedPhone = `(${usDigits.slice(0,3)}) ${usDigits.slice(3,6)}-${usDigits.slice(6,10)}`;
      } else if (usDigits.length >= 6) {
        formattedPhone = `(${usDigits.slice(0,3)}) ${usDigits.slice(3,6)}-${usDigits.slice(6)}`;
      } else if (usDigits.length >= 3) {
        formattedPhone = `(${usDigits.slice(0,3)}) ${usDigits.slice(3)}`;
      }
      setAccount({
        first_name: row.first_name || '',
        last_name: row.last_name || '',
        email: row.email || '',
        phone: formattedPhone,
      });
    } catch (e) {
      setAccount(null);
      setLoadError(getUserFacingError(e, 'profile-load'));
    } finally {
      setLoading(false);
    }
  }

  async function saveAccount() {
    if (!session?.userId) return;
    setAccountSaving(true);
    setError(null);
    try {
      const payload = { ...account };
      // empty strings to null
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      await userService.updateUser(session.userId, payload);
    } catch (e) {
      setError(getUserFacingError(e, 'profile-save'));
    } finally {
      setAccountSaving(false);
    }
  }

  async function loadIntake() {
    setLoading(true);
    setError(null);
    setLoadError(null);
    try {
      const res = await financialService.getProfile();
      setIntake(res?.data || {
        current_objective: null,
        filing_status: 'single',
        zip: '',
        birth_year: 1990,
        gross_income_exact: 0,
        pay_frequency: 'biweekly',
        risk_tolerance: null,
        housing_tenure: 'rent',
        employer_match_pct: null,
        employee_contribution_pct: null,
      });
    } catch (e) {
      setIntake(null);
      setLoadError(getUserFacingError(e, 'profile-load'));
    } finally {
      setLoading(false);
    }
  }

  function validateIntakeFields(data) {
    const errors = {};

    if (!data?.current_objective?.trim()) errors.current_objective = 'Objective is required';
    if (!data?.filing_status?.trim()) errors.filing_status = 'Filing status is required';
    if (!data?.zip || data.zip.length !== 5) errors.zip = 'ZIP code must be 5 digits';
    if (data?.birth_year == null) {
      errors.birth_year = 'Birth year is required';
    } else if (data.birth_year < 1900 || data.birth_year > 2100) {
      errors.birth_year = 'Birth year must be between 1900 and 2100';
    }
    if (data?.gross_income_exact == null) {
      errors.gross_income_exact = 'Gross income is required';
    } else if (data.gross_income_exact < 0) {
      errors.gross_income_exact = 'Income cannot be negative';
    }
    if (!data?.pay_frequency?.trim()) errors.pay_frequency = 'Pay frequency is required';
    if (!data?.risk_tolerance?.trim()) errors.risk_tolerance = 'Risk tolerance is required';
    if (!data?.housing_tenure?.trim()) errors.housing_tenure = 'Housing type is required';
    if (data?.employer_match_pct == null) {
      errors.employer_match_pct = 'Employer match % is required';
    } else if (data.employer_match_pct < 0 || data.employer_match_pct > 100) {
      errors.employer_match_pct = 'Match % must be between 0 and 100';
    }
    if (data?.employee_contribution_pct == null) {
      errors.employee_contribution_pct = 'Contribution % is required';
    } else if (data.employee_contribution_pct < 0 || data.employee_contribution_pct > 100) {
      errors.employee_contribution_pct = 'Contribution % must be between 0 and 100';
    }

    return errors;
  }

  function updateIntakeField(key, value) {
    setIntake((x) => ({ ...x, [key]: value }));
    if (intakeErrors[key]) {
      setIntakeErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  async function saveIntake() {
    const errors = validateIntakeFields(intake);
    setIntakeErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError({
        title: 'Check highlighted fields',
        message: 'Please fix the highlighted fields before saving.',
        severity: 'warning',
      });
      return;
    }

    setIntakeSaving(true);
    setError(null);
    try {
      const res = await financialService.upsertProfile(intake);
      if (!res?.ok) throw new Error(res?.error || 'Failed to save');
    } catch (e) {
      setError(getUserFacingError(e, 'profile-save'));
    } finally {
      setIntakeSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="profile-title">
      <div className="modal-card large">

        {/* Left nav */}
        <nav className="modal-nav" aria-label="Settings navigation">
          <div className="modal-nav-title">Settings</div>
          {resources.map((r) => {
            const isLocked = billingNotSetUp && (r.key === 'billing' || r.key === 'plaid');
            return (
              <button
                key={r.key}
                className={`modal-nav-item${r.key === tab ? ' active' : ''}${isLocked ? ' locked' : ''}`}
                onClick={() => {
                  if (isLocked) return;
                  setTab(r.key);
                  onTabChange?.(r.key);
                }}
                disabled={isLocked}
                title={isLocked ? 'Select a plan to unlock' : undefined}
              >
                {r.icon}
                {r.label}
              </button>
            );
          })}
          <div className="modal-nav-divider" role="separator"></div>
          <button className="modal-nav-item modal-nav-signout" onClick={logout}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Sign out
          </button>
        </nav>

        {/* Content area */}
        <div className="modal-content-area">
          <header className="modal-content-header">
            <h2 id="profile-title" className="modal-content-title">
              {resources.find((r) => r.key === tab)?.label ?? 'Settings'}
            </h2>
            <button className="icon-close" aria-label="Close" onClick={onClose}>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </header>

          <main className="modal-body">
            {error && <InlineAlert compact title={error.title} message={error.message} tone={error.severity || 'error'} />}

          {/* Account tab */}
          {tab === 'account' && (
            loading ? (
              <div className="grid" aria-label="Loading account">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <label className="field" key={`account-skeleton-${idx}`}>
                    <div className="field-label">
                      <Skeleton width="90px" height="12px" />
                    </div>
                    <div className="field-input">
                      <Skeleton variant="rectangular" height="36px" />
                    </div>
                  </label>
                ))}
                <div className="toolbar" style={{ gridColumn: '1 / -1', justifyContent: 'flex-end' }}>
                  <Skeleton variant="rectangular" width="140px" height="36px" />
                </div>
              </div>
            ) : loadError ? (
              <div className="settings-load-error">
                <InlineAlert
                  title={loadError.title}
                  message={loadError.message}
                  actionLabel="Try again"
                  onAction={loadAccount}
                />
              </div>
            ) : account ? (
              <div className="grid">
                <label className="field"><div className="field-label"><span>First name</span></div><div className="field-input"><input value={account?.first_name || ''} onChange={(e)=>setAccount((a)=>({...a, first_name:e.target.value}))} /></div></label>
                <label className="field"><div className="field-label"><span>Last name</span></div><div className="field-input"><input value={account?.last_name || ''} onChange={(e)=>setAccount((a)=>({...a, last_name:e.target.value}))} /></div></label>
                <label className="field"><div className="field-label"><span>Email</span></div><div className="field-input"><input type="email" value={account?.email || ''} onChange={(e)=>setAccount((a)=>({...a, email:e.target.value}))} /></div></label>
                <label className="field"><div className="field-label"><span>Phone</span></div><div className="field-input"><input 
                  type="tel"
                  value={account?.phone || ''} 
                  onChange={(e)=>{
                    const input = e.target.value;
                    const digits = input.replace(/\D/g, '');
                    // Handle US numbers with country code +1 (11 digits) or without (10 digits)
                    const usDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
                    const limitedDigits = usDigits.slice(0, 10);
                    let formatted = '';
                    if (limitedDigits.length === 0) {
                      formatted = '';
                    } else if (limitedDigits.length <= 3) {
                      formatted = limitedDigits;
                    } else if (limitedDigits.length <= 6) {
                      formatted = `(${limitedDigits.slice(0,3)}) ${limitedDigits.slice(3)}`;
                    } else {
                      formatted = `(${limitedDigits.slice(0,3)}) ${limitedDigits.slice(3,6)}-${limitedDigits.slice(6)}`;
                    }
                    setAccount((a)=>({...a, phone:formatted}));
                  }}
                  placeholder="(555) 123-4567"
                /></div></label>
                <div className="toolbar" style={{gridColumn:'1 / -1', justifyContent:'flex-end'}}>
                  <button className="btn btn-primary" onClick={saveAccount} disabled={accountSaving}>{accountSaving? 'Saving…':'Save changes'}</button>
                </div>
              </div>
            ) : null
          )}

          {/* Plaid tab */}
          {tab === 'plaid' && (
            <div className="plaid-tab">
              <PlaidAccountsList
                key={plaidRefreshKey}
                onRefresh={() => setPlaidRefreshKey((x) => x + 1)}
                needsPlaidConnect={needsPlaidConnect}
                onDismissPlaidConnect={onDismissPlaidConnect}
                onUpgradeRequired={onUpgradeRequired}
              />
            </div>
          )}

          {/* Intake tab */}
          {tab === 'intake' && (
            loading ? (
              <div className="grid" aria-label="Loading financial info">
                {Array.from({ length: 10 }).map((_, idx) => (
                  <label className="field" key={`intake-skeleton-${idx}`}>
                    <div className="field-label">
                      <Skeleton width="120px" height="12px" />
                    </div>
                    <div className="field-input">
                      <Skeleton variant="rectangular" height="36px" />
                    </div>
                  </label>
                ))}
                <div className="toolbar" style={{ gridColumn: '1 / -1', justifyContent: 'flex-end' }}>
                  <Skeleton variant="rectangular" width="140px" height="36px" />
                </div>
              </div>
            ) : loadError ? (
              <div className="settings-load-error">
                <InlineAlert
                  title={loadError.title}
                  message={loadError.message}
                  actionLabel="Try again"
                  onAction={loadIntake}
                />
              </div>
            ) : intake ? (
              <div className="intake-pane">
                {/* Violet callout */}
                <div className="intake-callout">
                  Your financial profile helps personalize every recommendation. Keep this up to date as your situation changes.
                </div>

                {/* Section 1 — Goals & Profile */}
                <div className="intake-section-label">Goals &amp; Profile</div>
                <div className="grid">
                  <label className={`field ${intakeErrors.current_objective ? 'field-error' : ''}`}>
                    <div className="field-label"><span>Investment Goal</span></div>
                    <div className="field-input">
                      <select value={intake?.current_objective || ''} onChange={(e) => updateIntakeField('current_objective', e.target.value || null)}>
                        <option value="">Select...</option>
                        <option value="Pay off debt">Pay off debt</option>
                        <option value="Save more">Save more</option>
                        <option value="Invest">Invest</option>
                        <option value="Increase income">Increase income</option>
                      </select>
                    </div>
                    {intakeErrors.current_objective && <div className="field-error-msg">{intakeErrors.current_objective}</div>}
                  </label>
                  <label className={`field ${intakeErrors.filing_status ? 'field-error' : ''}`}>
                    <div className="field-label"><span>Tax Filing Status</span></div>
                    <div className="field-input">
                      <select value={intake?.filing_status || 'single'} onChange={(e) => updateIntakeField('filing_status', e.target.value)}>
                        <option value="single">Single</option>
                        <option value="married_joint">Married Joint</option>
                        <option value="married_separate">Married Separate</option>
                        <option value="head_of_household">Head of Household</option>
                      </select>
                    </div>
                    {intakeErrors.filing_status && <div className="field-error-msg">{intakeErrors.filing_status}</div>}
                  </label>
                  <label className={`field ${intakeErrors.zip ? 'field-error' : ''}`}>
                    <div className="field-label"><span>ZIP Code</span></div>
                    <div className="field-input"><input inputMode="numeric" value={intake?.zip || ''} onChange={(e) => updateIntakeField('zip', e.target.value.slice(0, 5))} /></div>
                    {intakeErrors.zip && <div className="field-error-msg">{intakeErrors.zip}</div>}
                  </label>
                  <label className={`field ${intakeErrors.birth_year ? 'field-error' : ''}`}>
                    <div className="field-label"><span>Birth Year</span></div>
                    <div className="field-input"><input type="number" inputMode="numeric" value={intake?.birth_year ?? ''} onChange={(e) => updateIntakeField('birth_year', e.target.value === '' ? null : Number(e.target.value))} /></div>
                    {intakeErrors.birth_year && <div className="field-error-msg">{intakeErrors.birth_year}</div>}
                  </label>
                  <label className={`field ${intakeErrors.risk_tolerance ? 'field-error' : ''}`}>
                    <div className="field-label"><span>Risk Tolerance</span></div>
                    <div className="field-input">
                      <select value={intake?.risk_tolerance || ''} onChange={(e) => updateIntakeField('risk_tolerance', e.target.value || null)}>
                        <option value="">Select...</option>
                        <option value="conservative">Conservative</option>
                        <option value="moderate">Moderate</option>
                        <option value="aggressive">Aggressive</option>
                      </select>
                    </div>
                    {intakeErrors.risk_tolerance && <div className="field-error-msg">{intakeErrors.risk_tolerance}</div>}
                  </label>
                  <label className={`field ${intakeErrors.housing_tenure ? 'field-error' : ''}`}>
                    <div className="field-label"><span>Housing Type</span></div>
                    <div className="field-input">
                      <select value={intake?.housing_tenure || 'rent'} onChange={(e) => updateIntakeField('housing_tenure', e.target.value)}>
                        <option value="rent">Rent</option>
                        <option value="own">Own</option>
                      </select>
                    </div>
                    {intakeErrors.housing_tenure && <div className="field-error-msg">{intakeErrors.housing_tenure}</div>}
                  </label>
                </div>

                {/* Section 2 — Income & Retirement */}
                <div className="intake-section-label">Income &amp; Retirement</div>
                <div className="grid">
                  <label className={`field ${intakeErrors.gross_income_exact ? 'field-error' : ''}`}>
                    <div className="field-label"><span>Gross Annual Income ($)</span></div>
                    <div className="field-input">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={intake?.gross_income_exact == null ? '' : intake.gross_income_exact.toLocaleString('en-US')}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '');
                          updateIntakeField('gross_income_exact', digits === '' ? null : Number(digits));
                        }}
                      />
                    </div>
                    {intakeErrors.gross_income_exact && <div className="field-error-msg">{intakeErrors.gross_income_exact}</div>}
                  </label>
                  <label className={`field ${intakeErrors.pay_frequency ? 'field-error' : ''}`}>
                    <div className="field-label"><span>Pay Frequency</span></div>
                    <div className="field-input">
                      <select value={intake?.pay_frequency || 'biweekly'} onChange={(e) => updateIntakeField('pay_frequency', e.target.value)}>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="semimonthly">Semi-monthly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    {intakeErrors.pay_frequency && <div className="field-error-msg">{intakeErrors.pay_frequency}</div>}
                  </label>
                  <label className={`field ${intakeErrors.employer_match_pct ? 'field-error' : ''}`}>
                    <div className="field-label"><span>Employer 401k Match %</span></div>
                    <div className="field-input">
                      <input type="number" inputMode="decimal" value={intake?.employer_match_pct ?? ''} onChange={(e) => updateIntakeField('employer_match_pct', e.target.value === '' ? null : Number(e.target.value))} />
                    </div>
                    {intakeErrors.employer_match_pct && <div className="field-error-msg">{intakeErrors.employer_match_pct}</div>}
                  </label>
                  <label className={`field ${intakeErrors.employee_contribution_pct ? 'field-error' : ''}`}>
                    <div className="field-label"><span>401k Contribution %</span></div>
                    <div className="field-input">
                      <input type="number" inputMode="decimal" value={intake?.employee_contribution_pct ?? ''} onChange={(e) => updateIntakeField('employee_contribution_pct', e.target.value === '' ? null : Number(e.target.value))} />
                    </div>
                    {intakeErrors.employee_contribution_pct && <div className="field-error-msg">{intakeErrors.employee_contribution_pct}</div>}
                  </label>
                </div>

                <button className="btn btn-primary intake-save-btn" onClick={saveIntake} disabled={intakeSaving}>
                  {intakeSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            ) : null
          )}

          {/* Billing tab */}
          {tab === 'billing' && (
            <BillingDashboard onBillingChanged={refreshBilling} />
          )}
        </main>
        </div>

      </div>
    </div>
  );
}
